import { ENGINEERING_MODELS, type Point } from './models'

export const IMAGE_SIZE = 224
const thresholds = Float32Array.from({ length: IMAGE_SIZE ** 2 }, (_, i) => {
  const noise = (.06711056 * (i % IMAGE_SIZE) + .00583715 * Math.floor(i / IMAGE_SIZE)) % 1
  return (52.9829189 * noise) % 1
})
const extents = ENGINEERING_MODELS.map(model => Math.max(...model.segments.flat(2).map(Math.abs)))

/** Fill and light the 3D surfaces, then dither into a stable one-bit image. */
export function createRasterizer() {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = IMAGE_SIZE
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  const masks = ENGINEERING_MODELS.map(() => new Uint8Array(IMAGE_SIZE ** 2))
  return (modelIndex: number, time: number) => {
    const model = ENGINEERING_MODELS[modelIndex]
    const mask = masks[modelIndex]
    const scale = IMAGE_SIZE * .43 / extents[modelIndex]
    const yaw = -.35 + Math.sin(time * .1) * .18
    const pitch = .6 + Math.sin(time * .07) * .07
    const cy = Math.cos(yaw), sy = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch)
    const rotate = ([x, y, z]: Point): Point => {
      const rx = x * cy + z * sy, rz = z * cy - x * sy
      return [rx, y * cp - rz * sp, y * sp + rz * cp]
    }
    const project = ([x, y, z]: Point): [number, number] => {
      const perspective = 650 / (650 + z)
      return [IMAGE_SIZE / 2 + x * scale * perspective, IMAGE_SIZE / 2 + y * scale * perspective]
    }
    ctx.clearRect(0, 0, IMAGE_SIZE, IMAGE_SIZE)
    const faces = model.faces.map(face => ({ ...face, points: face.points.map(rotate) }))
    const depth = (points: Point[]) => points.reduce((sum, p) => sum + p[2], 0) / points.length
    faces.sort((a, b) => depth(b.points) - depth(a.points))
    for (const face of faces) {
      const [a, b, c] = face.points
      const u = b.map((v, i) => v - a[i]), v = c.map((value, i) => value - a[i])
      const normal = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]]
      const light = Math.abs((-normal[0] * .35 - normal[1] * .55 + normal[2] * .76) / (Math.hypot(...normal) || 1))
      const shade = Math.round(255 * face.tone * (.24 + .64 * light))
      ctx.fillStyle = `rgb(${shade},${shade},${shade})`
      ctx.beginPath()
      face.points.forEach((point, i) => {
        const [x, y] = project(point)
        if (i) ctx.lineTo(x, y)
        else ctx.moveTo(x, y)
      })
      ctx.closePath(); ctx.fill()
    }
    ctx.strokeStyle = '#b8b8b8'
    ctx.lineWidth = .65
    ctx.beginPath()
    for (const [a, b] of model.segments) {
      ctx.moveTo(...project(rotate(a))); ctx.lineTo(...project(rotate(b)))
    }
    ctx.stroke()
    const pixels = ctx.getImageData(0, 0, IMAGE_SIZE, IMAGE_SIZE).data
    for (let i = 0; i < mask.length; i++) {
      const brightness = pixels[i * 4] / 255 * pixels[i * 4 + 3] / 255
      mask[i] = brightness > 0 && brightness > thresholds[i] + (mask[i] ? -.025 : .025) ? 1 : 0
    }
    return mask
  }
}
